import { Routes, Route, Navigate, useParams } from "react-router-dom";

import HallOfEntities from "../../pages/my-cropwise";
import Protocols from "../../pages/protocols";
import CreateProtocol from "../../pages/create-protocol";
import TrialsList from "../../pages/trials";
import CreateTrials from "../../pages/new-trail";
import NewRatesAndDosage from "../../pages/new-rates-and-dosage";
import PageNotFound from "../../pages/page-not-found";
import { NewTrialStateProvider } from "../../pages/new-trail/hooks/NewTrialStateProvider";
import { useFeatureFlags, useLDConfig } from "../../context/LDFlagsContext";
import { Loader } from "../../shared/components/Loader";
import { appURLs } from "./appUrls";

const ScopedTrialsList = (): JSX.Element => {
  const { workspaceId, orgId, farmId } = useParams();
  const scopeKey = JSON.stringify([workspaceId, orgId, farmId]);

  return (
    <NewTrialStateProvider key={scopeKey}>
      <TrialsList />
    </NewTrialStateProvider>
  );
};

const ScopedProtocols = (): JSX.Element => {
  const { workspaceId, orgId, farmId } = useParams();
  const scopeKey = JSON.stringify([workspaceId, orgId, farmId]);

  return <Protocols key={scopeKey} />;
};

/**
 * define routes for the application
 * @returns routes for the application
 */
export const AppRoutes = (): JSX.Element => {
  const { isFlagsLoading } = useLDConfig();
  const { enableAutoProductAssignment } = useFeatureFlags();

  // The standalone Rates & Dosage screen is reachable only while its flag is
  // on. Gating the route as well as the Plot Design button that leads to it
  // matters because the URL is otherwise a way around the flag entirely -- it
  // takes a trial id, so it is guessable, and the screen is still half-built.
  //
  // Flags resolve asynchronously, so a bare `enableAutoProductAssignment`
  // check would redirect every user on first paint, before LaunchDarkly has
  // answered. Hold on a loader until it has.
  const newRatesAndDosageElement = ((): JSX.Element => {
    if (isFlagsLoading) {
      return <Loader mask message="" />;
    }
    if (!enableAutoProductAssignment) {
      return <Navigate to={appURLs.MY_CROPWISE} replace={true} />;
    }
    return (
      <NewTrialStateProvider>
        <NewRatesAndDosage />
      </NewTrialStateProvider>
    );
  })();

  const editTrialElement = isFlagsLoading ? (
    <Loader mask message="" />
  ) : (
    <NewTrialStateProvider>
      <CreateTrials />
    </NewTrialStateProvider>
  );

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={appURLs.MY_CROPWISE} replace={true} />}
      />
      <Route path={appURLs.MY_CROPWISE + "/*"} element={<HallOfEntities />} />
      <Route
        path={appURLs.HOME + "/*"}
        element={<Navigate to={appURLs.MY_CROPWISE} replace={true} />}
      />
      <Route
        path={
          appURLs.TRIALS +
          "/workspace/:workspaceId/org/:orgId/farm/:farmId" +
          "/*"
        }
        element={<ScopedTrialsList />}
      />
      <Route
        path={
          appURLs.TRIALS +
          "/workspace/:workspaceId/org/:orgId/farm/:farmId/create"
        }
        element={
          <NewTrialStateProvider>
            <CreateTrials />
          </NewTrialStateProvider>
        }
      />
      <Route
        path={
          appURLs.TRIALS +
          "/workspace/:workspaceId/org/:orgId/farm/:farmId/trial/:id"
        }
        element={editTrialElement}
      />
      <Route
        path={
          appURLs.TRIALS +
          "/workspace/:workspaceId/org/:orgId/farm/:farmId/trial/:id/new-rates-and-dosage"
        }
        element={newRatesAndDosageElement}
      />
      <Route
        path={
          appURLs.PROTOCOLS +
          "/workspace/:workspaceId/org/:orgId/farm/:farmId" +
          "/*"
        }
        element={<ScopedProtocols />}
      />
      <Route
        path={
          appURLs.PROTOCOLS +
          "/workspace/:workspaceId/org/:orgId/farm/:farmId/create/:step"
        }
        element={<CreateProtocol />}
      />
      <Route
        path={
          appURLs.PROTOCOLS +
          "/workspace/:workspaceId/org/:orgId/farm/:farmId/protocol/:protocolId/:step"
        }
        element={<CreateProtocol />}
      />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};
